import { useEffect, useState, useCallback, useRef } from 'react'
import { Form, message, Skeleton } from 'antd'
import { getTimeAsDayjs } from '@forwardflow/helpers/utils'
import { useBusinessHoursData } from '@forwardflow/hooks/customHooks'
import OrganizationService from '@forwardflow/services/OrganizationService'
import dayjs from 'dayjs'
import { BusinessHoursForm } from '@forwardflow/forms/BusinessHoursForm'
import useSetupProgress from '@forwardflow/hooks/customHooks/onboarding/useSetupProgress'
import { useConfirmationModalStore } from '@forwardflow/store/modal/confirmationModal'
import { Button as EditButton } from '@forwardflow/ui-v2'

const DAYS_OF_WEEK = [
   'Monday',
   'Tuesday',
   'Wednesday',
   'Thursday',
   'Friday',
   'Saturday',
   'Sunday',
]

const initializeBusinessHours = (data?: any) => {
   const days = DAYS_OF_WEEK.map((day, index) => ({
      day,
      enabled: data?.businessHours?.days?.[index]?.enabled ?? false,
      startTime: data?.businessHours?.days?.[index]?.startTime ?? '09:00',
      endTime: data?.businessHours?.days?.[index]?.endTime ?? '17:00',
   }))

   return {
      days,
      timezone: data?.businessHours?.timezone || '',
      allowAgentsToSetAvailability:
         data?.businessHours?.allowAgentsToSetAvailability || false,
   }
}

const determineScheduleType = (
   businessHoursData: any
): 'weekdays' | 'custom' | 'allWeek' | 'allWeekExceptSunday' => {
   if (!businessHoursData?.days) return 'weekdays'

   const weekdayTimes = businessHoursData.days.slice(0, 5)
   const allDaysEnabled = businessHoursData.days.every(
      (day: any) => day.enabled
   )
   const allWeekExceptSundayEnabled =
      businessHoursData.days.slice(0, 6).every((day: any) => day.enabled) &&
      !businessHoursData.days[6].enabled
   const hasConsistentWeekdays = weekdayTimes.every(
      (day: any) =>
         day.startTime === weekdayTimes[0].startTime &&
         day.endTime === weekdayTimes[0].endTime &&
         day.enabled
   )

   if (allDaysEnabled) return 'allWeek'
   if (allWeekExceptSundayEnabled) return 'allWeekExceptSunday'
   if (hasConsistentWeekdays) return 'weekdays'
   return 'custom'
}

const formatTime24to12 = (time24: string): string => {
   const [hours, minutes] = time24.split(':').map(Number);
   const period = hours >= 12 ? 'PM' : 'AM';
   const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
   return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const BusinessHours = () => {
   const [form] = Form.useForm()
   const { data } = useBusinessHoursData()

   const [isLoading, setIsLoading] = useState(false)
   const [isEditing, setIsEditing] = useState(false)
   const { openConfirmationModal } = useConfirmationModalStore()
   const { mutate } = useSetupProgress()
   const [scheduleType, setScheduleType] = useState<
      'weekdays' | 'custom' | 'allWeek' | 'allWeekExceptSunday'
   >(() => determineScheduleType(initializeBusinessHours(data)))

   const [businessHours, setBusinessHours] = useState(() =>
      initializeBusinessHours(data)
   )
   const [isDataReady, setIsDataReady] = useState(false)
   const savedStateRef = useRef(initializeBusinessHours(data))

   useEffect(() => {
      if (data) {
         const initializedData = initializeBusinessHours(data)
         setBusinessHours(initializedData)
         form.setFieldsValue(initializedData)
         setScheduleType(determineScheduleType(initializedData))
         savedStateRef.current = initializedData
         setIsDataReady(true)
      }
   }, [data, form])

   const handleOkClick = useCallback(
      async (e: React.MouseEvent<HTMLButtonElement>) => {
         e.preventDefault()
         setIsLoading(true)
         try {
            await form.validateFields()
            const submissionData = { ...businessHours }

            switch (scheduleType) {
               case 'weekdays':
                  submissionData.days = submissionData.days.map(
                     (day, index) => ({
                        ...day,
                        enabled: index < 5,
                     })
                  )
                  break
               case 'allWeek':
                  submissionData.days = submissionData.days.map((day) => ({
                     ...day,
                     enabled: true,
                  }))
                  break
               case 'allWeekExceptSunday':
                  submissionData.days = submissionData.days.map(
                     (day, index) => ({
                        ...day,
                        enabled: index !== 6,
                     })
                  )
                  break
               default:
                  break
            }

            await OrganizationService.updateBusinessHourData({
               businessHours: submissionData,
            })
            message.success('Business hours updated successfully!')
            setBusinessHours(submissionData)
            savedStateRef.current = submissionData
            openConfirmationModal({ title: 'Working Hours Configured!' })
         } catch (error) {
            console.error(
               'An error occurred while updating business hours:',
               error
            )
            message.error('Failed to update business hours. Please try again.')
            // Reset state to last saved data on failure
            setBusinessHours(savedStateRef.current)
            setScheduleType(determineScheduleType(savedStateRef.current))
            form.setFieldsValue(savedStateRef.current)
         } finally {
            setIsLoading(false)
            setIsEditing(false)
            mutate()
         }
      },
      [form, businessHours, scheduleType]
   )

   const handleCheckboxChange = useCallback(
      (index: number, checked: boolean) => {
         setBusinessHours((prevState) => {
            const updatedDays = [...prevState.days]
            updatedDays[index] = { ...updatedDays[index], enabled: checked }
            return { ...prevState, days: updatedDays }
         })
      },
      []
   )

   const validateTimeChange = (
      currentDay: any,
      newTime: string | null,
      type: 'startTime' | 'endTime'
   ) => {
      const startTime = getTimeAsDayjs(currentDay.startTime)
      const endTime = getTimeAsDayjs(currentDay.endTime)
      message.destroy()

      if (
         type === 'endTime' &&
         startTime &&
         newTime &&
         dayjs(newTime, 'HH:mm').isBefore(startTime)
      ) {
         message.error('End time must be after start time')
         return false
      }

      if (
         type === 'startTime' &&
         endTime &&
         newTime &&
         dayjs(newTime, 'HH:mm').isAfter(endTime)
      ) {
         message.error('Start time must be before end time')
         return false
      }

      return true
   }

   const handleTimeChange = useCallback(
      (index: number, time: any, type: 'startTime' | 'endTime') => {
         setBusinessHours((prevState) => {
            const updatedDays = [...prevState.days]
            const currentDay = updatedDays[index]
            const newTime = time ? time.format('HH:mm') : null

            if (!validateTimeChange(currentDay, newTime, type)) {
               return prevState
            }

            updatedDays[index] = {
               ...currentDay,
               [type]: newTime,
            }

            return { ...prevState, days: updatedDays }
         })
      },
      []
   )

   const handleAllWeekExceptSundayTimeChange = useCallback(
      (time: any, type: 'startTime' | 'endTime') => {
         setBusinessHours((prevState) => {
            const updatedDays = prevState.days.map((day, index) => {
               const newTime = time ? time.format('HH:mm') : null

               if (!validateTimeChange(day, newTime, type)) {
                  return day
               }

               return {
                  ...day,
                  [type]: newTime,
                  enabled: index !== 6,
               }
            })

            return { ...prevState, days: updatedDays }
         })
      },
      []
   )

   const handleAllWeekTimeChange = useCallback(
      (time: any, type: 'startTime' | 'endTime') => {
         setBusinessHours((prevState) => {
            const updatedDays = prevState.days.map((day) => {
               const newTime = time ? time.format('HH:mm') : null

               if (!validateTimeChange(day, newTime, type)) {
                  return day
               }

               return {
                  ...day,
                  [type]: newTime,
                  enabled: true,
               }
            })

            return { ...prevState, days: updatedDays }
         })
      },
      []
   )

   const handleWeekdayTimeChange = useCallback(
      (time: any, type: 'startTime' | 'endTime') => {
         setBusinessHours((prevState) => {
            const updatedDays = prevState.days.map((day, index) => {
               if (index >= 5) return { ...day, enabled: false }

               const newTime = time ? time.format('HH:mm') : null

               if (!validateTimeChange(day, newTime, type)) {
                  return day
               }

               return {
                  ...day,
                  [type]: newTime,
                  enabled: true,
               }
            })

            return { ...prevState, days: updatedDays }
         })
      },
      []
   )

   const handleScheduleTypeChange = useCallback(
      (type: 'weekdays' | 'custom' | 'allWeek' | 'allWeekExceptSunday') => {
         setScheduleType(type)
         if (type === 'custom') return

         // Normalize all days' times to match day[0] when switching to a uniform schedule
         setBusinessHours((prevState) => {
            const referenceStart = prevState.days[0].startTime
            const referenceEnd = prevState.days[0].endTime

            const updatedDays = prevState.days.map((day, index) => {
               let enabled = day.enabled
               if (type === 'weekdays') enabled = index < 5
               else if (type === 'allWeek') enabled = true
               else if (type === 'allWeekExceptSunday') enabled = index !== 6

               return {
                  ...day,
                  startTime: referenceStart,
                  endTime: referenceEnd,
                  enabled,
               }
            })

            return { ...prevState, days: updatedDays }
         })
      },
      []
   )

   const handleTimezoneChange = useCallback((value: string) => {
      setBusinessHours((prevState) => ({ ...prevState, timezone: value }))
   }, [])

   const renderBusinessHoursView = () => {
      return (
         <div className="mb-6 max-w-[800px]">
            <div className="px-8 pt-6 text-[14px]">
               <div className="flex items-center mb-4 gap-x-11">
                  <p className="w-20">Timezone </p>
                  <p className=" font-medium px-4 py-2 bg-[#828282]/10 border border-white/10 rounded">
                     {businessHours.timezone || 'Not set'}
                  </p>
               </div>

               <div className="grid grid-cols-1 gap-2">
                  {businessHours.days
                     .filter((day) => day.enabled)
                     .map((day) => (
                        <div
                           key={day.day}
                           className="flex items-center gap-x-11"
                        >
                           <div className="w-20">
                              <p>{day.day}</p>
                           </div>
                           <p className="font-medium px-4 py-2 bg-[#828282]/10 border border-white/10 rounded">
                              {formatTime24to12(day.startTime)} - {formatTime24to12(day.endTime)}
                           </p>
                        </div>
                     ))}
               </div>
            </div>
         </div>
      )
   }

   return (
      <div className="flex flex-col flex-1 h-full overflow-hidden">
         <div className="flex items-center justify-between h-20 pl-8 pr-4 bg-white/5 shrink-0">
            <span className="text-lg font-medium">Business Hours</span>
            {isDataReady && !isEditing && (
               <EditButton
                  onClick={() => setIsEditing(true)}
                  variant="secondary"
                  className="!h-10 !rounded !mt-0 flex items-center gap-4 max-w-32"
               >
                  Edit
               </EditButton>
            )}
         </div>
         <div className="flex-1 overflow-y-auto">
         {isDataReady ? (
            <div className="flex flex-col gap-y-1">
               {isEditing ? (
                  <section className="pt-0 pb-8 max-w-[800px]">
                     <BusinessHoursForm
                        title="Edit Business Hours"
                        description="Define working hours for Forward Flow to nurture leads"
                        saveButtonPlacement="top"
                        form={form}
                        businessHours={businessHours}
                        scheduleType={scheduleType}
                        setScheduleType={handleScheduleTypeChange}
                        handleTimezoneChange={handleTimezoneChange}
                        handleAllWeekExceptSundayTimeChange={
                           handleAllWeekExceptSundayTimeChange
                        }
                        handleAllWeekTimeChange={handleAllWeekTimeChange}
                        handleWeekdayTimeChange={handleWeekdayTimeChange}
                        handleCheckboxChange={handleCheckboxChange}
                        handleTimeChange={handleTimeChange}
                        handleOkClick={handleOkClick as any}
                        isLoading={isLoading}
                        handleCancel={() => {
                           setIsEditing(false)
                           setBusinessHours(savedStateRef.current)
                           setScheduleType(determineScheduleType(savedStateRef.current))
                           form.setFieldsValue(savedStateRef.current)
                        }}
                     />
                  </section>
               ) : (
                  renderBusinessHoursView()
               )}
            </div>
         ) : (
            <div className="p-8 max-w-[800px]">
               <Skeleton active className="mb-6" />
               <Skeleton active className="mb-6" />
               <Skeleton active />
            </div>
         )}
         </div>
      </div>
   )
}

export default BusinessHours
